'use client';

import React, { useEffect, useRef, useState } from 'react';

const INSTAGRAM_URL = 'https://www.instagram.com/barber_eleganze?igsh=MXk4NXh2eDZzc3Zy';
const TIKTOK_URL = 'https://www.tiktok.com/@eleganze_barber?_r=1&_t=ZN-94BXCZvfJp8';

interface VideoCardProps {
  videoSrc: string;
  posterSrc: string;
  altLabel: string;
  cta: string;
  aspectClass: string;
}

const InteractiveVideoCard: React.FC<VideoCardProps> = ({
  videoSrc,
  posterSrc,
  altLabel,
  cta,
  aspectClass,
}) => {
  const cardRef = useRef<HTMLButtonElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isHoverCapable, setIsHoverCapable] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const target = cardRef.current;
    if (!target) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (entry?.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin: '220px 0px' },
    );

    observer.observe(target);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const media = window.matchMedia('(hover: hover) and (pointer: fine)');
    const update = () => setIsHoverCapable(media.matches);
    update();
    media.addEventListener('change', update);
    return () => media.removeEventListener('change', update);
  }, []);

  const playVideo = async () => {
    if (!videoRef.current) return;
    try {
      await videoRef.current.play();
      setIsPlaying(true);
    } catch {
      setIsPlaying(false);
    }
  };

  const pauseVideo = () => {
    if (!videoRef.current) return;
    videoRef.current.pause();
    videoRef.current.currentTime = 0;
    setIsPlaying(false);
  };

  const toggleTouchPlayback = () => {
    if (isHoverCapable || !videoRef.current) return;
    if (videoRef.current.paused) {
      void playVideo();
      return;
    }
    pauseVideo();
  };

  return (
    <button
      ref={cardRef}
      type="button"
      aria-pressed={!isHoverCapable ? isPlaying : undefined}
      title={cta}
      onMouseEnter={() => {
        if (isHoverCapable) {
          void playVideo();
        }
      }}
      onMouseLeave={() => {
        if (isHoverCapable) {
          pauseVideo();
        }
      }}
      onClick={toggleTouchPlayback}
      className={`${aspectClass} w-full overflow-hidden rounded-sm bg-white relative group cursor-pointer block outline-none`}
    >
      <video
        ref={videoRef}
        src={isVisible ? videoSrc : undefined}
        poster={isVisible ? posterSrc : undefined}
        muted
        loop
        playsInline
        preload="metadata"
        className="w-full h-full object-cover"
      />
      {posterSrc && (
        <img
          src={isVisible ? posterSrc : undefined}
          alt={altLabel}
          loading="lazy"
          decoding="async"
          className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-200 ${
            isPlaying ? 'opacity-0 pointer-events-none' : 'opacity-100'
          }`}
        />
      )}
      <div className="absolute inset-0 bg-gradient-to-t from-black/48 via-black/20 to-black/5" />
      <div className="absolute inset-x-0 bottom-0 p-4 flex items-center justify-between">
        <span className="text-white text-xs sm:text-sm font-bold uppercase tracking-[0.15em]">{cta}</span>
        <span className="inline-flex h-8 w-8 rounded-full bg-white/90 text-[#1d1d1d] items-center justify-center">
          {isPlaying ? (
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
              <path d="M8 6h3v12H8zm5 0h3v12h-3z" />
            </svg>
          ) : (
            <svg className="w-4 h-4 translate-x-[1px]" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
              <path d="m8 5 11 7-11 7V5Z" />
            </svg>
          )}
        </span>
      </div>
      <div
        aria-hidden="true"
        className="absolute top-3 right-3 rounded-full bg-[#1d1d1d]/45 px-2.5 py-1 text-[10px] uppercase tracking-[0.15em] text-white/90"
      >
        {isHoverCapable ? 'Survolez' : 'Touchez'}
      </div>
    </button>
  );
};

export const SocialSection: React.FC = () => {
  return (
    <section className="relative w-full bg-white text-[#1d1d1d] py-24 px-6 md:px-12 border-t border-gray-100 overflow-hidden">
      <div className="absolute top-10 left-0 w-full text-center pointer-events-none opacity-[0.04]">
        <span className="text-[14vw] font-black uppercase leading-none">Réseaux sociaux</span>
      </div>

      <div className="max-w-7xl mx-auto relative z-10">
        <div className="mb-12 md:mb-20">
          <h2 className="text-4xl md:text-6xl lg:text-7xl font-black uppercase leading-[0.95] tracking-tight max-w-5xl">
            REJOIGNEZ LA COMMUNAUTÉ BARBER ELEGANZE. DÉCOUVREZ LES DERNIÈRES TENDANCES, TUTOS ET STYLES.
          </h2>
        </div>

        <div className="flex flex-col md:flex-row items-start md:items-center gap-4 md:gap-8 border-t border-gray-200 pt-6 mb-16">
          <span className="font-mono text-sm uppercase tracking-widest text-gray-500">SITUÉ EN FRANCE</span>
          <div className="hidden md:block h-px flex-1 bg-gray-200"></div>
          <span className="font-mono text-sm uppercase tracking-widest text-gray-500">SUIVEZ-NOUS @BARBER_ELEGANZE</span>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-12 md:gap-24 items-start">
          <div className="flex flex-col gap-4">
            <InteractiveVideoCard
              videoSrc="/images/video6.mp4"
              posterSrc=""
              altLabel="Vidéo TikTok du salon Eleganze"
              cta="Aperçu TikTok"
              aspectClass="aspect-[4/5]"
            />
            <div>
              <div className="flex flex-wrap items-baseline gap-x-1 gap-y-1">
                <h3 className="font-bold text-lg uppercase inline-flex items-center gap-2">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path fillRule="evenodd" d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 2.78-1.15 5.54-3.33 7.31-1.92 1.57-4.58 2.07-6.99 1.53-2.34-.52-4.41-2.01-5.53-4.13-1.15-2.17-1.25-4.84-.25-7.09 1.01-2.28 3.01-4.04 5.43-4.66 1.41-.36 2.91-.4 4.35-.15v4.02c-1.03-.19-2.12-.08-3.05.42-.95.5-1.68 1.35-2.02 2.34-.35 1.02-.31 2.18.12 3.16.42.97 1.25 1.74 2.23 2.13.98.39 2.1.4 3.09.06.97-.33 1.78-1.03 2.26-1.92.46-.86.64-1.85.64-2.84V.02z" clipRule="evenodd" />
                  </svg>
                  Événements TikTok
                </h3>
                <a
                  href={TIKTOK_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-mono text-sm text-gray-500 uppercase hover:text-[#1d1d1d] transition-colors leading-none"
                >
                  @eleganze_barber
                </a>
              </div>
            </div>
          </div>

          <div className="flex flex-col gap-4 md:mt-32">
            <InteractiveVideoCard
              videoSrc="/images/video3.mp4"
              posterSrc="/images/video3-poster.jpg"
              altLabel="Vidéo Instagram du salon Eleganze"
              cta="Aperçu Instagram"
              aspectClass="aspect-[4/3]"
            />

            <div className="mb-8">
              <div className="flex flex-wrap items-baseline gap-x-1 gap-y-1">
                <h3 className="font-bold text-lg uppercase inline-flex items-center gap-2">
                  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                    <path d="M7.75 2h8.5A5.75 5.75 0 0 1 22 7.75v8.5A5.75 5.75 0 0 1 16.25 22h-8.5A5.75 5.75 0 0 1 2 16.25v-8.5A5.75 5.75 0 0 1 7.75 2Zm8.5 1.8h-8.5A3.95 3.95 0 0 0 3.8 7.75v8.5a3.95 3.95 0 0 0 3.95 3.95h8.5a3.95 3.95 0 0 0 3.95-3.95v-8.5a3.95 3.95 0 0 0-3.95-3.95Zm-4.25 2.95a5.25 5.25 0 1 1 0 10.5 5.25 5.25 0 0 1 0-10.5Zm0 1.8a3.45 3.45 0 1 0 0 6.9 3.45 3.45 0 0 0 0-6.9Zm5.55-2.11a1.21 1.21 0 1 1 0 2.42 1.21 1.21 0 0 1 0-2.42Z" />
                  </svg>
                  Fil Instagram
                </h3>
                <a
                  href={INSTAGRAM_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-mono text-sm text-gray-500 uppercase hover:text-[#1d1d1d] transition-colors leading-none"
                >
                  @barber_eleganze
                </a>
              </div>
            </div>


          </div>
        </div>
      </div>
    </section>
  );
};
